import axios from "axios";
import { IFormData, IResponse } from "../configs/types";

export const IMAGE_URL = "https://api.regent-lighting.co.in/";
export const API_URL = "https://api.regent-lighting.co.in/api";
export const EXCEL_URL = "https://api.regent-lighting.co.in/";

const REMOTE_MEDIA_ORIGINS = [
  "http://api.regent-lighting.co.in/",
  "https://api.regent-lighting.co.in/",
  "http://regent-lighting.co.in/",
  "https://regent-lighting.co.in/",
];

const normalizeMediaPath = (pathOrUrl: string): string => {
  let path = pathOrUrl;

  for (const origin of REMOTE_MEDIA_ORIGINS) {
    if (pathOrUrl.startsWith(origin)) {
      path = pathOrUrl.slice(origin.length);
      break;
    }
  }

  return path.replace(/^\/+/, "");
};

/** Encode each path segment so spaces/special chars work in URLs (e.g. swiss-make-in%20india.png). */
export const encodeMediaPath = (pathOrUrl: string): string => {
  const normalized = normalizeMediaPath(pathOrUrl);
  return normalized
    .split("/")
    .filter(Boolean)
    .map((segment) => encodeURIComponent(decodeURIComponent(segment)))
    .join("/");
};

/** Direct API URL for on-screen display (production-proven). */
export const resolveDirectMediaUrl = (pathOrUrl: string): string =>
  `${IMAGE_URL}${encodeMediaPath(pathOrUrl)}`;

/** Same-origin proxy URL for PDF browser cache (no auth / no CORS). */
export const resolveMediaUrl = (pathOrUrl: string): string =>
  `/regent-media/${encodeMediaPath(pathOrUrl)}`;

/** @deprecated Use resolveMediaUrl — kept for PDF utilities. */
export const resolvePdfMediaUrl = resolveMediaUrl;

const defaultErrorResponse = {
  error: "Something went wrong! Please try again later.",
};

export const callApi = {
  get: async <T>(url: string): Promise<IResponse<T>> => {
    try {
      const response = await axios.get(API_URL + url);
      return response.data as any;
    } catch (error) {
      return {
        ...((error as any).response?.data || defaultErrorResponse),
        statusCode: (error as any).response?.status || 500,
      };
    }
  },

  post: async <T>(
    url: string,
    data?: IFormData,
    config?: any
  ): Promise<IResponse<T>> => {
    try {
      const response = await axios.post(API_URL + url, data, config);
      return response.data as any;
    } catch (error) {
      return {
        ...((error as any).response?.data || defaultErrorResponse),
        statusCode: (error as any).response?.status || 500,
      };
    }
  },

  put: async <T>(
    url: string,
    data?: IFormData,
    config?: any
  ): Promise<IResponse<T>> => {
    try {
      const response = await axios.put(API_URL + url, data, config);
      return response.data as any;
    } catch (error) {
      return {
        ...((error as any).response?.data || defaultErrorResponse),
        statusCode: (error as any).response?.status || 500,
      };
    }
  },

  patch: async <T>(
    url: string,
    data?: IFormData,
    config?: any
  ): Promise<IResponse<T>> => {
    try {
      const response = await axios.patch(API_URL + url, data, config);
      return response.data as any;
    } catch (error) {
      return {
        ...((error as any).response?.data || defaultErrorResponse),
        statusCode: (error as any).response?.status || 500,
      };
    }
  },

  delete: async <T>(url: string, config?: any): Promise<IResponse<T>> => {
    try {
      const response = await axios.delete(API_URL + url, config);
      return response.data || { message: "Success." };
    } catch (error) {
      return {
        ...((error as any).response?.data || defaultErrorResponse),
        statusCode: (error as any).response?.status || 500,
      };
    }
  },
};

axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    // Do something with request error
    return error.request?.data || { error: "Request Failed." };
  }
);

axios.interceptors.response.use(
  (response: any) => {
    response.data.statusCode = response.status;
    return response;
  },
  (error) => {
    throw error;
  }
);